import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ChevronRight, MapPin, Star, Shield, CheckCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";

const DESIGNATION_LABEL = {
  FOUNDING_HOME_CLUB: "Founding Home Club",
  LV_PARTNER: "LV Partner",
  LV_GLOBAL_PARTNER: "LV Global Partner",
};

const CATEGORY_ORDER = ["PLAY", "DINING", "STAY", "WELLNESS", "GOLF", "EXPERIENCES", "SHOPPING", "OTHER"];

function CategoryDivider({ label }) {
  return (
    <div className="flex items-center gap-4 mb-8" data-testid={`category-${label.toLowerCase()}`}>
      <div className="h-px flex-1 bg-gradient-to-r from-bronze/30 to-transparent" />
      <span className="text-[11px] sm:text-xs tracking-[0.25em] uppercase text-bronze font-medium whitespace-nowrap">
        {label}
      </span>
      <div className="h-px flex-1 bg-gradient-to-l from-bronze/30 to-transparent" />
    </div>
  );
}

function PartnerHero({ partner }) {
  const hasCover = !!partner.cover_image_url;
  const hasLogo = !!partner.logo_url;

  if (hasCover) {
    return (
      <div className="relative h-48 sm:h-56 overflow-hidden">
        <img
          src={partner.cover_image_url}
          alt={partner.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-walnut via-walnut/40 to-transparent" />
        {hasLogo && (
          <div className="absolute bottom-4 left-5 w-10 h-10 sm:w-12 sm:h-12 bg-cream/95 rounded-full flex items-center justify-center p-1.5 shadow-lg">
            <img src={partner.logo_url} alt={partner.name} className="w-full h-full object-contain" />
          </div>
        )}
      </div>
    );
  }

  if (hasLogo) {
    return (
      <div className="relative h-52 sm:h-60 bg-[#0a0806] flex items-center justify-center overflow-hidden">
        <img
          src={partner.logo_url}
          alt={partner.name}
          className="relative h-28 sm:h-36 w-auto object-contain"
        />
      </div>
    );
  }

  return (
    <div className="h-32 sm:h-40 bg-gradient-to-br from-walnut-deep to-walnut flex items-center justify-center">
      <span className="font-serif text-3xl text-bronze/20">{partner.name?.charAt(0)}</span>
    </div>
  );
}

function PartnerCard({ partner }) {
  const isFounding = partner.designation === "FOUNDING_HOME_CLUB";

  return (
    <Link
      to={`/privileges/${partner.id}`}
      className="block group"
      data-testid={`partner-card-${partner.slug}`}
    >
      <div className="bg-walnut border border-lv-border overflow-hidden transition-all duration-300 hover:border-bronze/40 hover:shadow-[0_4px_24px_rgba(168,121,74,0.08)]">
        <PartnerHero partner={partner} />

        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <h3 className="font-serif text-lg sm:text-xl text-cream tracking-wide group-hover:text-sand transition-colors">
                {partner.name}
              </h3>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] tracking-[0.2em] uppercase font-medium ${
                    isFounding ? "text-bronze" : "text-lv-muted"
                  }`}
                >
                  {isFounding && <Star size={10} className="fill-bronze/30" />}
                  {DESIGNATION_LABEL[partner.designation] || partner.designation}
                </span>
                {partner.is_featured && (
                  <span className="text-[9px] tracking-[0.15em] uppercase text-bronze/60 bg-bronze/8 px-2 py-0.5 border border-bronze/15">
                    Featured
                  </span>
                )}
              </div>
              {partner.location && (
                <div className="flex items-center gap-1.5 mt-2">
                  <MapPin size={12} className="text-lv-muted/60 shrink-0" />
                  <span className="text-[11px] text-lv-muted/70 tracking-wide">{partner.location}</span>
                </div>
              )}
            </div>
            <ChevronRight
              size={18}
              className="text-lv-muted/40 group-hover:text-bronze transition-colors shrink-0 mt-2"
            />
          </div>

          {partner.privileges?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-lv-border/50 space-y-2">
              {partner.privileges.slice(0, 3).map((pv) => (
                <div key={pv.id} className="flex items-start gap-2.5">
                  <CheckCircle size={13} className="text-lv-active shrink-0 mt-0.5" />
                  <span className="text-sm text-sand/80 leading-relaxed">{pv.title}</span>
                </div>
              ))}
              {partner.privileges.length > 3 && (
                <p className="text-xs text-lv-muted/60 pl-[22px]">
                  +{partner.privileges.length - 3} more
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function Privileges() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["privileges"],
    queryFn: () => api.get("/privileges"),
  });
  const { member } = useAuth();
  const [activeFilter, setActiveFilter] = useState("ALL");

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-12 h-0.5 bg-bronze mx-auto animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-32 px-6">
        <p className="text-cream text-sm">{error.message}</p>
      </div>
    );
  }

  const { chapter_name, chapter_city, partners } = data;

  // Collect available categories from the data
  const availableCategories = new Set();
  (partners || []).forEach((p) => {
    (p.categories?.length ? p.categories : ["OTHER"]).forEach((c) => availableCategories.add(c));
  });
  const sortedAvailable = CATEGORY_ORDER.filter((c) => availableCategories.has(c));

  // Filter partners by selected category
  const filteredPartners = activeFilter === "ALL"
    ? partners || []
    : (partners || []).filter((p) => {
        const cats = p.categories?.length ? p.categories : ["OTHER"];
        return cats.includes(activeFilter);
      });

  // Group by category
  const categoryGroups = {};
  filteredPartners.forEach((p) => {
    const cats = p.categories?.length ? p.categories : ["OTHER"];
    const relevantCats = activeFilter === "ALL" ? cats : cats.filter((c) => c === activeFilter);
    relevantCats.forEach((cat) => {
      if (!categoryGroups[cat]) categoryGroups[cat] = [];
      if (!categoryGroups[cat].find((x) => x.id === p.id)) categoryGroups[cat].push(p);
    });
  });

  const sortedCats = Object.keys(categoryGroups).sort((a, b) => {
    const ai = CATEGORY_ORDER.indexOf(a);
    const bi = CATEGORY_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return (
    <div data-testid="privileges-page" className="max-w-5xl mx-auto">
      {/* Page Header */}
      <div className="mb-10 sm:mb-14">
        <h1 className="font-serif text-3xl sm:text-4xl text-cream tracking-wide">
          Privileges Network
        </h1>
        <div className="flex items-center gap-3 mt-3">
          <MapPin size={14} className="text-bronze shrink-0" />
          <p className="text-sm text-lv-muted tracking-wide">
            {chapter_name}{chapter_city ? ` \u00b7 ${chapter_city}` : ""}
          </p>
        </div>
        {member?.status === "ACTIVE" && (
          <div className="flex items-center gap-2 mt-4">
            <Shield size={13} className="text-lv-active" />
            <span className="text-[11px] tracking-[0.15em] uppercase text-lv-active font-medium">
              Active Member
            </span>
          </div>
        )}
      </div>

      {/* Category Filter */}
      {sortedAvailable.length > 1 && (
        <div className="mb-10 -mx-4 px-4 overflow-x-auto" data-testid="category-filter">
          <div className="flex gap-2 min-w-max pb-1">
            <button
              onClick={() => setActiveFilter("ALL")}
              className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase transition-colors border whitespace-nowrap ${
                activeFilter === "ALL"
                  ? "bg-bronze/15 border-bronze/30 text-cream"
                  : "bg-transparent border-lv-border text-lv-muted hover:border-bronze/20"
              }`}
              data-testid="filter-all"
            >
              All
            </button>
            {sortedAvailable.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveFilter(cat)}
                className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase transition-colors border whitespace-nowrap ${
                  activeFilter === cat
                    ? "bg-bronze/15 border-bronze/30 text-cream"
                    : "bg-transparent border-lv-border text-lv-muted hover:border-bronze/20"
                }`}
                data-testid={`filter-${cat.toLowerCase()}`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {filteredPartners.length === 0 && (
        <div className="text-center py-20">
          <p className="text-lv-muted text-sm tracking-wide">
            No privileges available{activeFilter !== "ALL" ? ` in ${activeFilter}` : " for your chapter yet"}.
          </p>
        </div>
      )}

      {/* Category Sections */}
      <div className="space-y-12 sm:space-y-16">
        {sortedCats.map((cat) => (
          <div key={cat}>
            <CategoryDivider label={cat} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8">
              {categoryGroups[cat].map((p) => (
                <PartnerCard key={p.id} partner={p} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
